import { I1GPenalty, TUserRole } from "types";

export type TNavitaireSupplier = "6E" | "SG";
export type TPaxType = "ADT" | "CHD" | "INF";
export type TBookingStatus = | "SUCCESS" | "TICKETING_PENDING" | "FAILED" | "CANCELLED" | "PENDING" | "REFUNDED" | "TO_BE_REFUNDED";


/* API RESPONSE BODY */
export interface IResponse<T> {
    status: boolean;
    message: string;
    response: T;
};

export interface IResponseFormat<T> {
    isSuccess: boolean;
    data: T;
    error: T;
    status: number;
    message?: string;
};

export const res: IResponseFormat<undefined> = {
    isSuccess: false,
    data: undefined,
    error: undefined,
    status: 0,
    message: '',
};

/* LOGIN RESPONSE */
export interface IUserAuthorities {
    name: string;
    actionAllowed: {
        read: boolean;
        print: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
        download: boolean;
    };
};


export interface IMasterData {
    id?: number;
    name: string;
};

export interface IStaticMaster {
    key: string;
    value: string | number;
};

/* ADMIN MASTER */
export interface IGstList {
    id?: number;
    travelServiceCategory: string;
    gst: number;
}
export interface IApplyFor {
    id?: number;
    name: string;
    travelServiceCategory: string;
    formType: string;
    status?: boolean;
}
export interface IFlightMarkupList {
    id?: number;
    name: string;
    travelServiceCategory: string;
    formType: string;
}

export interface IFareType {
    id?: number,
    name: string
    status?: boolean
}
export interface IApplyOn {
    id?: number;
    name: string;
    status?: boolean;
}
export interface ISearchType {
    id?: number;
    name: string;
    status?: boolean;
}
export interface IApplyType {
    id?: number;
    name: string;
    status?: boolean;
}
export interface IOriginCountry {
    id?: number;
    name: string;
    status?: boolean;
}
export interface ISupplier {
    id?: number;
    name: string;
    code: string;
    status?: boolean;
};

export interface ICountryMaster {
    id: number;
    name: string;
    code: string;
};

export interface IMarkupFee {
    id?: number;
    travelServiceCategory: string;
    formType: string;
    feeType: {
        id?: number;
        name: string;
    },
    status?: boolean
}

export interface IBookingTypeList {
    id?: number;
    name: string;
    code: string;
    flightTicketSupplierId: number;
    supplierId?: number;
    supplierName?: string;
    supplierCode?: string;
    supplierStatus?: boolean
    status?: boolean;
}

export interface IHotelsCategory {
    id?: number;
    name: string;
    status?: boolean
}



type ResponseType = {
    id: number;
    name: string;
};

type TravelFormFeeResponse = {
    id: number;
    feeAmount: number;
    feeType: 'FIXED' | 'PERCENTAGE';
};

export interface IFlightBookingList {
    id: number;
    affiliateGroup: {
        id: number | null;
        name: string | null;
    };
    chargeApplyForType: {
        id: number;
        name: string;
    };
    bookingType: {
        id: number;
        name: string;
    };
    startDate: string; // or Date if you prefer Date objects
    endDate: string;   // or Date if you prefer Date objects
    minimumGrossPrice: number;
    maximumGrossPrice: number;
    userType: string;
    gst: boolean;
    status: boolean;

    // flight booking list data

    flightAirlineResponses: {
        id: number;
        name: string;
    }[];
    originCountryResponses: {
        id: number;
        name: string;
    }[];
    destinationCountryResponses: {
        id: number;
        name: string;
    }[];
    originAirportResponses: {
        id: number | null;
        name: string | null;
    }[];
    destinationAirportResponses: {
        id: number | null;
        name: string | null;
    }[];
    travelServiceFormFeeResponses: {
        id: number;
        feeAmount: number;
        feeType: 'FIXED' | 'PERCENTAGE';
        name: string
    }[];
    supplierResponses: {
        id: number;
        name: string;
    }[];
    chargeApplyOnType: {
        id: number;
        name: string;
    };
    flightFareType: {
        id: number;
        name: string;
    };
    flightJourneyType: {
        id: number;
        name: string;
    };
    flightApplyType: {
        id: number;
        name: string;
    };
    flightClass: string[];
    flightCabin: string[];

    // Hotel Booking List Data

    countryResponses: ResponseType[];
    placeResponses: ResponseType[];
    travelFormFeeResponses: TravelFormFeeResponse[];
    hotelBookingSupplierResponses: ResponseType[];
    hotelCategoryResponses: ResponseType[];
};


export interface IFlightOnlineBookingPaymentOrder {
    id: number;
    orderId: string;
    paymentSessionId: string;
    travelServiceCategory: string;
    naviatorOrderDetail: {
        indigoBasePrice: number | null;
        indigoTaxPrice: number;
        indigoSsrPrice: number;
        indigoSeatPrice: number;
    };
    galelioFlightOrderDetail: {
        flightBasePrice: number;
        flightTaxPrice: number;
    };
    akbarOrderDetail: {
        akbarBasePrice: number;
        akbarTaxPrice: number;
        akbarSsrPrice: number;
        akbarSeatPrice: number;
    };
    adminOtherCharge: number;
    adminTotalGst: number;
    adminBaseCharge: number;
    adminServiceCharge: number;
    adminBookingCharge: number;
    paymentCharge: number;
    discount: number;
    paymentStatus: string;
    affiliateOtherCharges: number;
    affiliateTotalGst: number;
    affiliateBaseCharge: number;
    affiliateServiceCharge: number;
    affiliateBookingCharge: number;
    commissionBaseFare: number;
    commissionSurcharge: number;
    commissionOtherTaxes: number;
    commissionIncentive: number;
    commissionPlbBaseFare: number;
    commissionPlbYQ: number;
    commissionPlbYR: number;
    commissionPlbBaseFareYQ: number;
    commissionPlbBaseFareYR: number;
    commissionCashback: number;
    commissionTds: number;
};

export interface IFlightOnlineBookingResponseList {
    id: number;
    referenceNo: string;
    invoiceNo: string;
    // galileo booking details
    galileoBookingDetailResponses: {
        origin: string;
        destination: string;
        bookingStatus: TBookingStatus;
        flightBookingSegments: {
            group: number;
            distance: number;
            participantLevel: string;
            linkAvailability: boolean;
            polledAvailabilityOption: string;
            optionalServicesIndicator: boolean;
            availabilitySource: string;
            availabilityDisplayType: string;
            carrier: string;
            changeOfPlane: boolean;
            classOfService: string;
            equipment: string;
            flightNumber: string;
            flightTime: number;
            fareInfoRef: string;
            origin: string;
            destination: string;
            departureTime: string;
            arrivalTime: string;
            originTerminal: string | null;
            destinationTerminal: string | null;
            cabin: string;
            maxWeight: number;
            unit: string;
            numberOfPieces: number | null;
            brandName: string | null;
            flightBookingImages: string[];
            originAirportId: number | null;
            destinationAirportId: number | null;
            originPlaceId: number | null;
            destinationPlaceId: number | null;
            eticketability: string;
        }[];
        bookingChangePenalty: I1GPenalty[] | null;
        bookingCancelPenalty: I1GPenalty[] | null;
        bookingFareRules: IFareRule[];
        cancellationChargeResponse: {
            supplierCharge: number | null;
            cancellationCharge: number | null;
            cancellationChargeGst: number | null;
        };
    }[] | null;
    galileoPassengerDetailResponses: {
        title: string;
        firstName: string;
        lastName: string;
        gender: string;
        dateOfBirth: string;
        passportExpiryDate: string | null;
        passportIssueDate: string | null;
        passportNumber: string | null;
    }[] | null;
    // navitaire booking details
    navitaireBookingDetailResponses: {
        origin: string;
        destination: string;
        bookingStatus: TBookingStatus;
        navitaireBookingDetailSegments: {
            origin: string;
            destination: string;
            originDateTime: string;
            destinationDateTime: string;
            carrierCode: string;
            flightNumber: string;
            segmentSellKey: string;
            international: boolean;
            adjustedCapacity: number;
            originTerminal: string;
            destinationTerminal: string;
            arrvLTV: number;
            backMoveDays: number;
            capacity: number;
            changeOfDirection: boolean;
            deptLTV: number;
            equipmentType: string;
            equipmentTypeSuffix: string;
            flifoUpdated: boolean;
            irop: boolean;
            actualOffBlockTime: string;
            actualOnBlockTime: string;
            actualTouchDownTime: string;
            airborneTime: string;
            arrivalGate: string;
            arrivalNote: string;
            arrivalStatus: string;
            baggageClaim: string;
            departureGate: string;
            departureNote: string;
            departureStatus: string;
            eta: string;
            etd: string;
            tailNumber: string;
            originAirportId: string;
            destinationAirportId: string;
            originPlaceId: string;
            destinationPlaceId: string;
            eticket: boolean;
            onTime: string;
        }[];
        cancellationChargeResponse: {
            supplierCharge: number | null;
            cancellationCharge: number | null;
            cancellationChargeGst: number | null;
        };
    }[] | null;
    navitairePassengerDetailResponses: {
        title: string;
        firstName: string;
        lastName: string;
        gender: string;
        dateOfBirth: string;
        passportExpiryDate: string | null;
        passportIssueDate: string | null;
        passportNumber: string | null;
        infantDetail: {
            title: string;
            firstName: string;
            lastName: string;
            gender: string;
            dateOfBirth: string;
            passportNumber: string | null;
            passportIssueDate: string | null;
            passportExpiryDate: string | null;
        } | null;
    }[] | null;
    // akbar booking details
    akbarBookingDetailResponses: {
        origin: string;
        destination: string;
        bookingStatus: TBookingStatus;
        akbarBookingDetailJourneys: IAkbarFlightDetails[];
        cancellationChargeResponse: {
            supplierCharge: number | null;
            cancellationCharge: number | null;
            cancellationChargeGst: number | null;
        };
    }[] | null;
    akbarPassengerDetailResponses: {
        paxId: number;
        title: string;
        firstName: string;
        lastName: string;
        gender: string;
        dateOfBirth: string;
        passengerCode: string;
        passportNumber: string | null;
        passportIssueDate: string | null;
        passportExpiryDate: string | null;
        infantDetail: {
            paxId: number;
            title: string;
            firstName: string;
            lastName: string;
            gender: string;
            dateOfBirth: string;
            passportNumber: string | null;
            passportIssueDate: string | null;
            passportExpiryDate: string | null;
        } | null;
    }[] | null;
    pnrDetail: {
        supplierPnr: string | null;
        providerPnr: string | null;
        ticketNumber: string | null;
    };
    locatorCode: {
        universalRecordLocatorCode: string | null;
        providerReservationInfoLocatorCode: string | null;
        airReservationLocatorCode: string | null;
    };
    user: {
        id: number;
        name: {
            firstName: string;
            middleName: string;
            lastName: string;
        };
        emailId: string;
        phoneNumber: string;
        address: string;
        city: string;
        pinCode: string;
    };
    bookingMode: string;
    flightSupplierId: number;
    flightSupplierName: string;
    bookedBy: string;
    bookingDate: string;
    // cancellationChargeResponse: {
    //     supplierCharge: number | null;
    //     cancellationCharge: number | null;
    // };
};

export interface IFareRule {
    fareInfoRef: string;
    ruleNumber: string;
    source: string;
    tariffNumber: string;
    providerCode: string;
    fareRuleLongs: IFareRuleLongs[];
};

export interface IFareRuleLongs {
    category: string;
    type: string;
    value: string;
};

export interface IBookingUserDetails {
    id: number;
    name: {
        firstName: string | null;
        middleName: string | null;
        lastName: string | null;
    };
    emailId: string | null;
    phoneNumber: string | null;
    address: string | null;
    city: string | null;
    pinCode: string | null;
    companyName: string | null;
};

export interface IFlightOnlineBookingList {
    uuid: string;
    id: number;
    traceId: string;
    createdDate: string;
    contactNumber: string | null;
    email: string | null;
    invoiceNo: string | null;
    refundNo: string | null;
    bookingResponses: IFlightOnlineBookingResponseList[];
    paymentOrder: IFlightOnlineBookingPaymentOrder;
    bookedBy: TUserRole;
    userResponse: IBookingUserDetails;
};

export interface INavitaireFlightJourneyResponseList {
    fareSellKey: string;
    journeySellKey: string;
    markupId: number | null;
    totalPrice: number;
    flightSegmentResponseList: INavitaireFlightSegmentResponseList[];
};

// flight segment
export interface INavitaireFlightSegmentResponseList {
    origin: string;
    destination: string;
    originDateTime: string;
    destinationDateTime: string;
    carrierCode: TNavitaireSupplier;
    flightNumber: string;
    segmentSellKey: string;
    international: boolean;
    adjustedCapacity: number;
    originTerminal: string | null;
    destinationTerminal: string | null;
    arrvLTV: number | null;
    backMoveDays: number | null;
    capacity: number | null;
    changeOfDirection: false,
    deptLTV: number | null;
    equipmentType: string;
    equipmentTypeSuffix: string | null;
    flifoUpdated: boolean;
    irop: boolean;
    actualOffBlockTime: any;
    actualOnBlockTime: any;
    actualTouchDownTime: any;
    airborneTime: any;
    arrivalGate: any;
    arrivalNote: any;
    arrivalStatus: any;
    baggageClaim: any;
    departureGate: any;
    departureNote: any;
    departureStatus: any;
    eta: any;
    etd: any;
    tailNumber: any;
    eticket: boolean;
    onTime: string | null;
};


//    ONLINE BOOKING LIST HOTEL | FLIGHT | BUS
export interface IHotelOnlineBookingList {
    id: number;
    traceId: string;
    bookingRooms: {
        guestList: {
            salutation: string;
            firstName: string;
            lastName: string;
            countryId: number;
            panCardNo: string;
            passPortNumber: string;
            dateOfIssue: string | null;
            dateOfExpiry: string | null;
        }[];
        roomName: string;
        roomId: string | null;
        boardNameResponses: {
            roomCount: number;
            boardName: string;
            boardCode: string | null;
        }[];
        roomRateKey: {
            cancellationPolicies: {
                amount?: string;
                hotelAmount?: string;
                percent?: number;
                numberOfNights?: number;
                from: string;
            }[];
            promotions: {
                code: string;
                name: string;
                remark: string;
            }[];
            rateKey: string | null;
            rateComment: string | null;
            totalPrice: number;
        };
        wildCards: string[];
        hotelBookingFacilities: {
            name: string;
            feeApplied: boolean;
            mandatory: boolean;
            facilityGroup: string;
        }[];
        roomImageUrl: string[];
        user: string | null;
    }[];
    referenceNo: string;
    invoiceNo: string;
    pnr: string;
    email: string;
    contactNumber: string;
    bookingStatus: TBookingStatus;
    bookingMode: string;
    bookingDate: string;
    createdDate: string;
    paymentOrderResponse: THotelOnLineBookingPaymentOrder;
    cancellationChargeResponse: {
        supplierCharge: number | null;
        cancellationCharge: number | null;
        cancellationChargeGst: number | null;
    };
    bookedBy: TUserRole;
    user: IBookingUserDetails;
};

export interface IBookingTimeline {
    createdBy: string;
    name: string;
    email: string;
    createdAt: string;
    bookingStatus: TBookingStatus;
    userType: "NORMAL_USER" | "ADMIN" | "AFFILIATE";
    remark: string | null;
    systemGenerated: boolean;
};


export type THotelOnLineBookingPaymentOrder = {
    // id: string | null;
    // orderId: string | null;
    // paymentSessionId: string;
    // travelServiceCategory: string;
    // hotelOrderDetail: {
    //     totalRoomCharge: number;
    //     checkIn: string;
    //     checkOut: string;
    // };
    // hotel: {
    //     id: number;
    //     name: string;
    //     description: string;
    //     address: string;
    //     postalCode: string;
    //     email: string;
    //     phones: Array<{
    //         phoneNumber: string;
    //         phoneType: string;
    //     }>;
    //     city: string;
    //     place: {
    //         id: number;
    //         name: string;
    //         status: boolean;
    //     };
    // };
    // otherCharge: number;
    // totalGst: number;
    // baseCharge: number;
    // serviceCharge: number;
    // bookingCharge: number;
    // paymentCharge: number;
    // discount: number;
    // paymentStatus: string;
    // hotelTotalAmount: number;
    id: number;
    orderId: string | null;
    paymentSessionId: string | null;
    travelServiceCategory: string;
    hotelOrderDetail: {
        totalRoomCharge: number;
        checkIn: string;
        checkOut: string;
    };
    hotel: {
        id: number;
        name: string;
        description: string;
        address: string;
        postalCode: string;
        email: string;
        phones: Array<{
            phoneNumber: string;
            phoneType: string;
        }>;
        city: string;
        place: {
            id: number;
            name: string;
            status: boolean;
        };
    };
    adminOtherCharge: number;
    adminTotalGst: number;
    adminBaseCharge: number;
    adminServiceCharge: number;
    adminBookingCharge: number;
    paymentCharge: number;
    discount: number;
    paymentStatus: string;
    hotelTotalAmount: number;
    affiliateOtherCharges: number;
    affiliateTotalGst: number;
    affiliateBaseCharge: number;
    affiliateServiceCharge: number;
    affiliateBookingCharge: number
};

export type THotelsMarkupB2B = {
    id: number;
    countryResponses: {
        id: number;
        name: string;
    }[];
    placeResponses: {
        id: number;
        name: string;
    }[];
    travelFormFeeResponses: {
        id: number;
        feeAmount: number;
        name: string;
        feeType: "FIXED" | "PERCENTAGE";

    }[];
    affiliateGroup: {
        id: number;
        name: string;
    };
    hotelBookingSupplierResponses: {
        id: number;
        name: string;
    }[];
    chargeApplyForType: {
        id: number;
        name: string;
    };
    bookingType: {
        id: number;
        name: string;
    };
    startDate: string;  // ISO string date format
    endDate: string;    // ISO string date format
    minimumGrossPrice: number;
    maximumGrossPrice: number;
    gst: boolean;
    hotelCategoryResponses: {
        id: number;
        name: string;
    }[];
    userType: "ADMIN_CUSTOMER" | "AFFILIATE_CUSTOMER" | "AFFILIATE";  // Assuming possible values
    status: boolean;
};

export interface IFlightCommissionResponse {
    id: number;
    startDate: string;
    endDate: string;
    status: boolean;
    originCountry: {
        id: number;
        name: string;
    }[];
    destinationCountry: {
        id: number;
        name: string;
    }[];
    originAirport: {
        id: number;
        name: string;
    }[];
    destinationAirport: {
        id: number;
        name: string;
    }[];
    ticketSupplier: {
        id: number;
        name: string;
    }[];
    flightAirline: {
        id: number;
        name: string;
    }[];
    affiliateGroup: {
        id: number;
        name: string;
    }[];
    flightClass: {
        key: string;
        value: string | number;
    }[];
    flightCabin: {
        key: string;
        value: string | number;
    }[];
    flightTypes: {
        key: string;
        value: string | number;
    }[];
    flightSearchTypes: {
        key: string;
        value: string | number;
    }[];
    commissionChargeWithAmountTypes: {
        commissionFormCharge: {
            key: string;
            value: string;
        },
        amount: number;
        feeType: "FIXED" | "PERCENTAGE";
    }[];
};

export interface IAffiliatePaymentRequestResponse {
    id: number;
    depositType: string;
    depositAmount: number | null;
    dateOfDeposit: string | null;
    beneficiaryBank: string | null;
    beneficiaryName: string | null;
    remarks: string | null;
    chequeDdNumber: string | null;
    chequeDdIssueDate: string | null;
    depositorBankAccountNumber: string | null;
    depositorBankName: string | null;
    depositorBankBranch: string | null;
};

export interface IUserDetailsForInvoice {
    id: number;
    name: string;
    emailId: string;
    phoneNumber: string;
    affiliateDetailForInvoice: {
        country: string | null;
        city: string | null;
        state: string | null;
        pinCode: string | null;
        firstAddress: string | null;
        secondAddress: string | null;
        companyName: string | null;
    };
};

export interface IBookingExternalAPILog {
    id: number;
    apiName: string;
    apiProvider: string;
    apiType: "xml" | "json";
    requestPayloadMediaId: number;
    responsePayloadMediaId: number;
    traceId: string;
    paymentInitiated: boolean;
};

export interface IAkbarFlightDetails {
    Segments: {
        origin: string;
        destination: string;
        FUID: string;
        VAC: string;
        Index: string;
        SSR: {
            paxId: number;
            Code: string;
            Description: string;
            PieceDescription: string;
            Charge: number;
            VAT: number;
            Type: string;
            Category: string;
            PTC: string;
            ID: number;
            IsFreeMeal: boolean;
            MealImage: string;
            SSRUrl: string | null;
            AdditionalFields: any[];
        }[];
    }[];
    stops: number; // Number of Stops. Stop information is available in Connections leg with Type = "C"
    seats: number;
    returnIdentifier: number;
    index: string;
    provider: string;
    flightNo: string;
    vac: string;
    mac: string; // Marketing Airline Code
    oac: string;
    arrivalTime: string;
    departureTime: string;
    arrivalTerminal: string;
    departureTerminal: string;
    fareClass: string;
    duration: string;
    groupCount: number;
    totalFare: number | null;
    grossFare: number;
    totalCommission: number;
    totalTransactionFee: number;
    totalVatOnTFee: number;
    netFare: number;
    actualFare: number;
    wpNetFare: number;
    hops: number; // Number of Hops. "DEL-SXR" flight has hop at "ATQ" means that the flight has a stop at "ATQ" but the passenger can remain in the same Aircraft. Hopping information is available in Connections leg with Type = "H"
    notice: string;
    noticeLink: string;
    noticeType: string;
    refundable: string;
    alliances: string;
    amenities: string;
    inclusions: {
        Baggage: string | null;
        Meals: string | null;
        PieceDescription: string | null
    };
    hold: boolean;
    holdInfo: string;
    connections: {
        Airport: string;
        ArrAirportName: string;
        Duration: string;
        Type: "C" | "H"; // C - Connection, H - Hopping
        MAC: string; // Marketing Airline Code
        VAC: string;
        OAC: string;
        FlightNo: string;
        From: string | null;
        To: string | null
    }[];
    from: string;
    to: string;
    fromName: string;
    toName: string;
    airlineName: string;
    gdsPriority: number;
    aircraft: string;
    rbd: string;
    cabin: string;
    fbc: string;
    fcBegin: string;
    fcEnd: string;
    fcType: string;
    fcGroup: string;
    gfl: boolean;
    promo: string | null;
    recommended: boolean;
    fareType: string;
    trendFare: number;
    channelCode: string | null;
    wpIndex: string | null;
    journeyKey: string;
    fareLink: string | null;
    misEShare: string;
    moreFltKey: string;
    wpRegionCode: string;
    remarks: string;
    adminMarkupId: number | null;
    affiliateMarkupId: number | null;
    seniorCitizen: boolean;
    busStation: boolean;
};